import React, { useState, useEffect } from 'react';
import { Building2, Calendar, TrendingUp, TrendingDown, Trophy, Mail, Loader2, ArrowDownUp } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { buildTopLists, fmtINR } from '../utils/portfolio';
import { exportHoldingsListToExcel } from '../utils/excelExport';
import { Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pctFromHigh = (cur, high) => (high && high > 0 ? ((cur - high) / high) * 100 : null);
const PctFrom = ({ cur, high, label }) => {
  const v = pctFromHigh(cur, high);
  if (v == null) return null;
  return <div className={`text-xs ${v >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{v >= 0 ? '+' : ''}{v.toFixed(1)}% {label}</div>;
};

const RankTable = ({ rows, priceStats }) => (
  <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50 hover:bg-slate-50">
            <TableHead className="font-semibold text-slate-700 w-10">#</TableHead>
            <TableHead className="font-semibold text-slate-700">Stock</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">Current Value</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">Today's Gain</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">Overall Gain</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">52W High</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">52W Low</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">All-Time High</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">All-Time Low</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-6">No data</TableCell></TableRow>
          )}
          {rows.map((h, i) => {
            const st = (priceStats || {})[h.ticker] || {};
            return (
              <TableRow key={`${h.ticker}-${h.instrumentType}`} className="hover:bg-slate-50">
                <TableCell className="text-slate-500">{i + 1}</TableCell>
                <TableCell>
                  <div className="font-semibold text-slate-900">{h.ticker}</div>
                  <div className="text-xs text-slate-500 truncate max-w-[180px]">{h.shareName}</div>
                </TableCell>
                <TableCell className="text-right text-blue-600 font-medium">₹{fmtINR(h.currentValue)}</TableCell>
                <TableCell className={`text-right font-medium ${(h.dayChange || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {h.hasPrevClose ? (
                    <>
                      <div>{(h.dayChange || 0) > 0 ? '+' : ''}₹{fmtINR(h.dayChange)}</div>
                      <div className="text-xs">{(h.dayChangePercent || 0) > 0 ? '+' : ''}{(h.dayChangePercent || 0).toFixed(2)}%</div>
                    </>
                  ) : <span className="text-slate-400">-</span>}
                </TableCell>
                <TableCell className={`text-right font-bold ${h.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  <div>{h.profit >= 0 ? '+' : ''}₹{fmtINR(h.profit)}</div>
                  <div className="text-xs">{(h.returnPercentage || 0) > 0 ? '+' : ''}{(h.returnPercentage || 0).toFixed(2)}%</div>
                </TableCell>
                <TableCell className="text-right text-slate-700 text-sm">
                  {st.week52High > 0 ? <><div className="text-emerald-600">₹{fmtINR(st.week52High)}</div><PctFrom cur={h.currentMarketPrice} high={st.week52High} label="off" /></> : <span className="text-slate-400">-</span>}
                </TableCell>
                <TableCell className="text-right text-slate-700 text-sm">
                  {st.week52Low > 0 ? <span className="text-rose-600">₹{fmtINR(st.week52Low)}</span> : <span className="text-slate-400">-</span>}
                </TableCell>
                <TableCell className="text-right text-slate-700 text-sm">
                  {st.allTimeHigh > 0 ? <><div className="text-emerald-600">₹{fmtINR(st.allTimeHigh)}</div><PctFrom cur={h.currentMarketPrice} high={st.allTimeHigh} label="off" /></> : <span className="text-slate-400">-</span>}
                </TableCell>
                <TableCell className="text-right text-slate-700 text-sm">
                  {st.allTimeLow > 0 ? <span className="text-rose-600">₹{fmtINR(st.allTimeLow)}</span> : <span className="text-slate-400">-</span>}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  </div>
);

const Section = ({ icon: Icon, title, color, children }) => (
  <div className="mb-8">
    <h3 className={`text-base font-bold mb-3 flex items-center gap-2 ${color}`}><Icon className="h-5 w-5" /> {title}</h3>
    {children}
  </div>
);

// Sort options for the holdings list.
const SORT_OPTIONS = [
  { value: 'currentValue', label: 'Latest Value' },
  { value: 'dayChange', label: "Today's Gain (₹)" },
  { value: 'dayChangePercent', label: "Today's Gain %" },
  { value: 'profit', label: 'Overall Gain (₹)' },
  { value: 'returnPercentage', label: 'Overall Gain %' },
  { value: 'shareName', label: 'Company Name (A–Z)' },
];

const HoldingsTable = ({ rows, sortKey, priceStats }) => (
  <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50 hover:bg-slate-50">
            <TableHead className="font-semibold text-slate-700 w-10">#</TableHead>
            <TableHead className="font-semibold text-slate-700">Stock</TableHead>
            <TableHead className="font-semibold text-right text-slate-700">Qty</TableHead>
            <TableHead className={`font-semibold text-right ${sortKey === 'shareName' ? 'text-indigo-700' : 'text-slate-700'}`}>Avg Cost</TableHead>
            <TableHead className="font-semibold text-right text-slate-700">Prev. Close</TableHead>
            <TableHead className="font-semibold text-right text-slate-700">LTP</TableHead>
            <TableHead className={`font-semibold text-right ${sortKey === 'currentValue' ? 'text-indigo-700' : 'text-slate-700'}`}>Latest Value</TableHead>
            <TableHead className={`font-semibold text-right ${sortKey === 'dayChange' || sortKey === 'dayChangePercent' ? 'text-indigo-700' : 'text-slate-700'}`}>Today's G/L</TableHead>
            <TableHead className={`font-semibold text-right ${sortKey === 'profit' || sortKey === 'returnPercentage' ? 'text-indigo-700' : 'text-slate-700'}`}>Overall Gain</TableHead>
            <TableHead className="font-semibold text-right text-slate-700">52W High</TableHead>
            <TableHead className="font-semibold text-right text-slate-700">52W Low</TableHead>
            <TableHead className="font-semibold text-right text-slate-700">All-Time High</TableHead>
            <TableHead className="font-semibold text-right text-slate-700">All-Time Low</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={13} className="text-center text-slate-400 py-6">No holdings</TableCell></TableRow>
          )}
          {rows.map((h, i) => {
            const st = (priceStats || {})[h.ticker] || {};
            return (
            <TableRow key={`${h.ticker}-${h.instrumentType}`} className="hover:bg-slate-50" data-testid={`holdings-row-${h.ticker}`}>
              <TableCell className="text-slate-500">{i + 1}</TableCell>
              <TableCell>
                <div className="font-semibold text-slate-900">{h.ticker}</div>
                <div className="text-xs text-slate-500 truncate max-w-[200px]">{h.shareName}</div>
              </TableCell>
              <TableCell className="text-right text-slate-700">{(h.quantity || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</TableCell>
              <TableCell className="text-right text-slate-700">₹{fmtINR(h.avgPrice)}</TableCell>
              <TableCell className="text-right text-slate-500">{h.previousClose ? `₹${fmtINR(h.previousClose)}` : '-'}</TableCell>
              <TableCell className="text-right text-slate-700">₹{fmtINR(h.currentMarketPrice)}</TableCell>
              <TableCell className="text-right text-blue-600 font-medium">₹{fmtINR(h.currentValue)}</TableCell>
              <TableCell className={`text-right font-medium ${(h.dayChange || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {h.hasPrevClose ? (
                  <>
                    <div>{(h.dayChange || 0) > 0 ? '+' : ''}₹{fmtINR(h.dayChange)}</div>
                    <div className="text-xs">{(h.dayChangePercent || 0) > 0 ? '+' : ''}{(h.dayChangePercent || 0).toFixed(2)}%</div>
                  </>
                ) : <span className="text-slate-400">-</span>}
              </TableCell>
              <TableCell className={`text-right font-bold ${h.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                <div>{h.profit >= 0 ? '+' : ''}₹{fmtINR(h.profit)}</div>
                <div className="text-xs">{(h.returnPercentage || 0) > 0 ? '+' : ''}{(h.returnPercentage || 0).toFixed(2)}%</div>
              </TableCell>
              <TableCell className="text-right text-slate-700 text-sm">
                {st.week52High > 0
                  ? <><div className="text-emerald-600">₹{fmtINR(st.week52High)}</div><PctFrom cur={h.currentMarketPrice} high={st.week52High} label="off" /></>
                  : <span className="text-slate-400">-</span>}
              </TableCell>
              <TableCell className="text-right text-slate-700 text-sm">
                {st.week52Low > 0
                  ? <span className="text-rose-600">₹{fmtINR(st.week52Low)}</span>
                  : <span className="text-slate-400">-</span>}
              </TableCell>
              <TableCell className="text-right text-slate-700 text-sm">
                {st.allTimeHigh > 0
                  ? <><div className="text-emerald-600">₹{fmtINR(st.allTimeHigh)}</div><PctFrom cur={h.currentMarketPrice} high={st.allTimeHigh} label="off" /></>
                  : <span className="text-slate-400">-</span>}
              </TableCell>
              <TableCell className="text-right text-slate-700 text-sm">
                {st.allTimeLow > 0
                  ? <span className="text-rose-600">₹{fmtINR(st.allTimeLow)}</span>
                  : <span className="text-slate-400">-</span>}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  </div>
);

const DailySummary = ({ transactions, entityInfo, summary, onEmail, emailSending, portfolioId, onEnriched }) => {
  const currentDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  const lists = buildTopLists(transactions);
  const s = summary || {};
  const [sortKey, setSortKey] = useState('currentValue');
  const [priceStats, setPriceStats] = useState({});

  useEffect(() => {
    let active = true;
    let attempts = 0;
    let reloaded = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/holdings/price-stats`, { params: { portfolioId: portfolioId || 'all' } });
        if (!active) return;
        const data = r.data && r.data.stats ? r.data.stats : (r.data || {});
        const pending = r.data && typeof r.data.pending === 'number' ? r.data.pending : 0;
        setPriceStats(data);
        if (!reloaded && Object.keys(data).length > 0 && onEnriched) { reloaded = true; onEnriched(); }
        attempts += 1;
        if (pending > 0 && attempts < 30) setTimeout(poll, 4000);
        else if (pending === 0 && onEnriched) onEnriched();
      } catch (e) {
        attempts += 1;
        if (active && attempts < 30) setTimeout(poll, 4000);
      }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, transactions.length]);

  const sortedHoldings = [...lists.holdings].sort((a, b) => {
    if (sortKey === 'shareName') return (a.shareName || '').localeCompare(b.shareName || '');
    return (b[sortKey] || 0) - (a[sortKey] || 0);
  });

  const Metric = ({ label, value, cls, sub }) => (
    <div>
      <p className="text-xs text-slate-600 mb-1">{label}</p>
      <p className={`text-lg font-bold ${cls || 'text-slate-900'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );

  return (
    <Card className="border-2 border-indigo-200 shadow-xl bg-gradient-to-br from-white to-indigo-50" data-testid="daily-summary-report">
      <CardHeader className="border-b-2 border-indigo-200 bg-indigo-50">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-2xl font-bold text-slate-900 mb-2">Daily Portfolio Summary</CardTitle>
            <div className="space-y-1 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4" /><span className="font-semibold">{entityInfo.entityName}</span></div>
              <div>Portfolio ID: {entityInfo.portfolioId} | PAN: {entityInfo.panNumber}</div>
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4" /><span>Report Date: {currentDate}</span></div>
            </div>
          </div>
          <Badge className="bg-indigo-100 text-indigo-700 text-sm px-3 py-1">{lists.holdings.length} Stocks</Badge>
        </div>
        {onEmail && (
          <div className="mt-4">
            <Button
              onClick={onEmail}
              disabled={emailSending || lists.holdings.length === 0}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="email-summary-btn"
            >
              {emailSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              {emailSending ? 'Sending...' : 'Email Me This Summary'}
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-6">
        {/* Top metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8 p-4 bg-white rounded-lg border border-slate-200">
          <Metric label="Total Invested" value={`₹${fmtINR(s.totalInvestment)}`} />
          <Metric label="Current Value" value={`₹${fmtINR(s.totalCurrentValue)}`} cls="text-blue-600" />
          <Metric label="Overall P/L" value={`${(s.totalProfit || 0) >= 0 ? '+' : ''}₹${fmtINR(s.totalProfit)}`} cls={(s.totalProfit || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'} sub={`${(s.totalReturnPercent || 0) > 0 ? '+' : ''}${s.totalReturnPercent || 0}%`} />
          <Metric label="Today's Gain/Loss" value={`${(s.todaysGainLoss || 0) >= 0 ? '+' : ''}₹${fmtINR(s.todaysGainLoss)}`} cls={(s.todaysGainLoss || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'} sub={`${(s.todaysGainLossPercent || 0) > 0 ? '+' : ''}${s.todaysGainLossPercent || 0}%`} />
          <Metric label="Portfolio CAGR (XIRR)" value={s.xirr != null ? `${s.xirr > 0 ? '+' : ''}${s.xirr}%` : 'N/A'} cls={(s.xirr || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'} />
        </div>

        {/* Sortable holdings list */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <h3 className="text-base font-bold flex items-center gap-2 text-indigo-700"><Trophy className="h-5 w-5" /> Holdings ({sortedHoldings.length})</h3>
            <div className="flex items-center gap-2">
              <ArrowDownUp className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-600">Sort by</span>
              <Select value={sortKey} onValueChange={setSortKey}>
                <SelectTrigger className="w-52 bg-white" data-testid="daily-summary-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`sort-opt-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => exportHoldingsListToExcel(sortedHoldings, entityInfo, (SORT_OPTIONS.find((o) => o.value === sortKey) || {}).label, priceStats)}
                disabled={sortedHoldings.length === 0}
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                data-testid="export-holdings-list-btn"
              >
                <Download className="mr-2 h-4 w-4" /> Export
              </Button>
            </div>
          </div>
          <HoldingsTable rows={sortedHoldings} sortKey={sortKey} priceStats={priceStats} />
        </div>

        <Section icon={TrendingUp} title="Top 25 Gainers (by % Return)" color="text-emerald-700">
          <RankTable rows={lists.topGainersPct} priceStats={priceStats} />
        </Section>
        <Section icon={TrendingDown} title="Top 25 Losers (by % Return)" color="text-rose-700">
          <RankTable rows={lists.topLosersPct} priceStats={priceStats} />
        </Section>

        <Section icon={TrendingUp} title="Top 25 Gainers (Today's ₹ Gain/Loss)" color="text-emerald-700">
          <RankTable rows={lists.topGainersDay} priceStats={priceStats} />
        </Section>
        <Section icon={TrendingDown} title="Top 25 Losers (Today's ₹ Gain/Loss)" color="text-rose-700">
          <RankTable rows={lists.topLosersDay} priceStats={priceStats} />
        </Section>

        {lists.holdings.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <p className="text-lg">No active holdings</p>
            <p className="text-sm mt-1">Add holdings to see your daily summary</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DailySummary;
